/**
 * Attachment Utility Functions
 *
 * Pure utility functions for working with attachments.
 * No side effects, fully testable.
 */

import {
  type Attachment,
  type CreateAttachmentOptions,
  type UpdateAttachmentOptions,
  type UploadStatus
} from "@nexus/types"
import { isImageFile, isValidStatusTransition } from "@nexus/types/core"

import { generateId } from "@/common/utils/generate-id"

/**
 * Extract file extension from filename
 *
 * @param filename - File name with extension
 * @returns File extension (without dot) or empty string
 *
 * @example
 * getFileExtension('document.pdf') // 'pdf'
 * getFileExtension('photo.backup.jpg') // 'jpg'
 * getFileExtension('noextension') // ''
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split(".")
  const extension = parts[parts.length - 1]
  return parts.length > 1 && extension ? extension.toLowerCase() : ""
}

/**
 * Extension to MIME type mapping for common file types
 * Used as fallback when File.type is not available or is generic
 */
const EXTENSION_MIME_MAP: Record<string, string> = {
  // Documents
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  rtf: "application/rtf",
  md: "text/markdown",
  // Spreadsheets
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  // Presentations
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/x-m4a",
  // Video
  mp4: "video/mp4",
  webm: "video/webm",
  // Data
  json: "application/json",
  xml: "application/xml",
  html: "text/html",
  css: "text/css",
  js: "application/javascript",
  // Email
  eml: "message/rfc822"
}

/**
 * Infer MIME type from filename extension
 *
 * @param filename - File name with extension
 * @returns MIME type string or "application/octet-stream" as fallback
 *
 * @example
 * getMimeTypeFromFilename('document.pdf') // 'application/pdf'
 * getMimeTypeFromFilename('data.xlsx') // 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
 * getMimeTypeFromFilename('unknown.xyz') // 'application/octet-stream'
 */
export function getMimeTypeFromFilename(filename: string): string {
  const extension = getFileExtension(filename)
  return EXTENSION_MIME_MAP[extension] ?? "application/octet-stream"
}

/**
 * Create a new attachment from a browser File
 *
 * Initializes attachment in PENDING state, ready for upload.
 *
 * @param options - Attachment creation options
 * @returns New attachment entity
 *
 * @example
 * const file = new File(['content'], 'doc.pdf', { type: 'application/pdf' })
 * const attachment = createAttachment({ file })
 */
export function createAttachment(options: CreateAttachmentOptions): Attachment {
  const { file, id = generateId() } = options

  return {
    id,
    file,
    status: "PENDING",
    isImage: isImageFile(file.name, file.type),
    metadata: {
      name: file.name,
      size: file.size,
      type: file.type,
      extension: getFileExtension(file.name)
    }
  }
}

/**
 * Update an attachment with new properties
 *
 * Creates a new attachment instance (immutable update).
 * Validates status transitions before applying.
 *
 * @param attachment - Current attachment
 * @param updates - Properties to update
 * @returns New attachment with updates applied
 * @throws Error if status transition is invalid
 *
 * @example
 * const updated = updateAttachment(attachment, {
 *   status: 'UPLOADING'
 * })
 */
export function updateAttachment(
  attachment: Attachment,
  updates: UpdateAttachmentOptions
): Attachment {
  // Validate status transition if status is being updated
  if (updates.status && updates.status !== attachment.status) {
    if (!isValidStatusTransition(attachment.status, updates.status)) {
      throw new Error(
        `Invalid status transition: ${attachment.status} → ${updates.status} for attachment ${attachment.id}`
      )
    }
  }

  return {
    ...attachment,
    ...updates,
    // Set uploadedAt timestamp when marking as COMPLETED
    ...(updates.status === "COMPLETED" && { uploadedAt: new Date() })
  }
}

/**
 * Mark attachment as uploading
 *
 * @param attachment - Current attachment
 * @returns Updated attachment in UPLOADING state
 */
export function markAsUploading(attachment: Attachment): Attachment {
  return updateAttachment(attachment, { status: "UPLOADING" })
}

/**
 * Mark attachment as completed with backend data
 *
 * @param attachment - Current attachment
 * @param knowledgeId - Knowledge ID from backend
 * @param url - Short-lived signed URL (for images); the stored object is private
 * @param thumbnailUrl - Thumbnail URL (for images)
 * @returns Updated attachment in COMPLETED state
 */
export function markAsCompleted(
  attachment: Attachment,
  knowledgeId: string,
  url?: string,
  thumbnailUrl?: string
): Attachment {
  return updateAttachment(attachment, {
    status: "COMPLETED",
    knowledgeId,
    url,
    thumbnailUrl,
    error: undefined // Clear any previous errors
  })
}

/**
 * Mark attachment as failed with error message
 *
 * @param attachment - Current attachment
 * @param error - Error message
 * @returns Updated attachment in FAILED state
 */
export function markAsFailed(attachment: Attachment, error: string): Attachment {
  return updateAttachment(attachment, {
    status: "FAILED",
    error
  })
}

/**
 * Mark attachment as cancelled
 *
 * @param attachment - Current attachment
 * @returns Updated attachment in CANCELLED state
 */
export function markAsCancelled(attachment: Attachment): Attachment {
  return updateAttachment(attachment, {
    status: "CANCELLED"
  })
}

/**
 * Check if attachment can be attached to a message
 *
 * Attachment is ready if upload is complete and successful.
 *
 * @param attachment - Attachment to check
 * @returns True if attachment can be sent with message
 */
export function canAttachToMessage(attachment: Attachment): boolean {
  return attachment.status === "COMPLETED" && !!attachment.knowledgeId
}

/**
 * Check if attachment is in a terminal state
 *
 * Terminal states: COMPLETED, FAILED, CANCELLED
 *
 * @param attachment - Attachment to check
 * @returns True if attachment is in terminal state
 */
export function isTerminalState(attachment: Attachment): boolean {
  return ["COMPLETED", "FAILED", "CANCELLED"].includes(attachment.status)
}

/**
 * Get user-friendly status label
 *
 * @param status - Upload status
 * @returns Human-readable status label
 */
export function getStatusLabel(status: UploadStatus): string {
  const labels: Record<UploadStatus, string> = {
    PENDING: "Pending",
    UPLOADING: "Uploading...",
    PROCESSING: "Processing...",
    COMPLETED: "Completed",
    FAILED: "Failed",
    CANCELLED: "Cancelled"
  }
  return labels[status]
}
